import React, { useRef } from "react";

import { useDispatch } from "react-redux";

import { Alert, Button, Label } from "@busbud/horizon";

import type { PassengerGender } from "@app/constants/passengers";
import { useAppContext } from "@app/helpers/hooks";
import type { TranslatorService } from "@app/services/translator";
import type { NapiPassenger, NapiUser } from "@app/types/napi-types";

import type { Field, PassengerFormType, State } from "./usePassengerForm";

import { AddPassenger } from "./add-passenger";
import { PassengerForm } from "./passenger-form";
import { usePassengerForm } from "./usePassengerForm";
import * as Validator from "../../../../../helpers/input-validator";
import { getErrorText } from "../../../../../modules/account/utils";
import { openModal } from "../../../../../store/state/modals";
import {
  addedPassengerProfile,
  confirmPassengerFormUpdate,
  removedPassengerProfile,
  submittedUpdateAccountHolderForm,
  updatedPassengerProfile
} from "../../../../../tracking/account-tracking";
import { AccountLayout } from "../../account-layout";
import { AccountVerification } from "../../account-verification";

interface Props {
  user: NapiUser;
  passengers: NapiPassenger[];
  updateUser: (user: NapiUser) => void;
}

type Form = "account_holder" | "new_passenger" | "passenger";

const fields_validation = {
  first_name: Validator.firstName,
  last_name: Validator.lastName
};

const getAlertMessage = (
  translator: TranslatorService,
  submit_status_user: "success" | string
) => {
  if (submit_status_user === "success") {
    return translator.t("!accounts.edit-profile.messages.success");
  }
  return getErrorText(translator, submit_status_user);
};

const getSeverity = (state: string): "positive" | "negative" => {
  return state === "success" ? "positive" : "negative";
};

export const PassengersApp: React.FC<Props> = props => {
  const { api, tracker, translator } = useAppContext();
  const reduxDispatch = useDispatch();
  const add_passenger_form_ref = useRef<HTMLDivElement>(null);
  // @ts-expect-error
  const [state, dispatch]: [State, React.Dispatch<any>] =
    usePassengerForm(props);
  const new_passenger_placeholder_name = translator.t(
    "!checkout.passengers.traveler-no-category",
    {
      number: state.passengers.length + 1
    }
  );

  const updateUser = (user: State["account_holder"]) => {
    tracker.track(confirmPassengerFormUpdate("main_passenger"));
    dispatch({ type: "load" });
    updateUserData(user);
  };

  const updateUserData = (user: State["account_holder"]) => {
    const data = {
      ...user,
      first_name: user.first_name.value,
      last_name: user.last_name.value
    };

    if (
      props.user.first_name !== data.first_name ||
      props.user.last_name !== data.last_name
    ) {
      return api
        .updateUser(data.id, data)
        .then(res => {
          if (res.body.error) {
            return showUserError(res.body.error);
          }
          tracker.track(submittedUpdateAccountHolderForm());
          props.updateUser(res.body);
          dispatch({ type: "update_user" });
        })
        .catch(showUserError);
    }
  };

  const handleChangeValue =
    (form: Form, passenger_id?: number) =>
    (field: string, event: undefined | React.ChangeEvent<{ value: unknown }>) =>
      dispatch({
        type: "change",
        form,
        field,
        passenger_id,
        value: event?.target.value
      });

  const hasFormError =
    (form: Form, passenger?: PassengerFormType) => (field: string) => {
      let error: { name: string } | null;
      if (
        form === "passenger" &&
        passenger &&
        passenger[field as keyof PassengerFormType]
      ) {
        error = fields_validation[field as keyof typeof fields_validation](
          (passenger[field as keyof typeof passenger] as any)?.value
        );
      } else {
        const form_state = state[form];
        if (!form_state || !form_state[field as keyof typeof form_state]) {
          return false;
        }
        error = fields_validation[field as keyof typeof fields_validation](
          (form_state[field as keyof typeof form_state] as any)?.value
        );
      }

      if (!error) {
        return false;
      }

      let error_text = getErrorText(translator, `${field}.${error.name}`);
      if (
        form !== "account_holder" &&
        (field === "first_name" || field === "last_name")
      ) {
        error_text = getErrorText(translator, "FieldRequired");
      }

      dispatch({
        type: "error",
        form,
        field,
        passenger,
        error: error_text
      });

      return true;
    };

  const passengersUpdate = <
    T extends {
      first_name: Field<string>;
      last_name: Field<string>;
      gender?: Field<PassengerGender>;
    }
  >(
    passenger: T
  ): T & {
    first_name: string;
    last_name: string;
    gender?: PassengerGender;
  } => {
    dispatch({ type: "load" });

    return {
      ...passenger,
      first_name: passenger.first_name.value,
      last_name: passenger.last_name.value,
      ...(passenger.gender
        ? { gender: passenger.gender.value }
        : { gender: undefined })
    };
  };

  const addPassenger = (passenger: PassengerFormType) => {
    tracker.track(confirmPassengerFormUpdate("saved_passenger"));
    const data = passengersUpdate(passenger);

    api
      .addPassenger(data)
      .then((res: { error: { type: string } }) => {
        if (res.error) {
          return showPassengersError(res.error);
        }
        tracker.track(addedPassengerProfile());
        dispatch({
          type: "add_passenger",
          passenger: { ...res, ...passenger }
        });
      })
      .catch(showPassengersError);
  };

  const updatePassenger = (passenger: PassengerFormType) => {
    tracker.track(confirmPassengerFormUpdate("saved_passenger"));
    const data = passengersUpdate(passenger);

    api
      .updatePassenger(data)
      .then((res: { error: { type: string } }) => {
        if (res.error) {
          return showPassengersError(res.error);
        }
        tracker.track(updatedPassengerProfile());
        dispatch({
          type: "update_passenger",
          passenger
        });
      })
      .catch(showPassengersError);
  };

  const showUserError = (error: { type?: string }) => {
    dispatch({
      type: "submit_error_user",
      error_type: error.type
    });
  };

  const showPassengersError = (error: { type: string }) => {
    dispatch({
      type: "submit_error_passengers",
      error_type: error.type
    });
  };

  const validateForms = () => {
    let account_holder_has_error = false;
    let new_passenger_has_error = false;

    // Check Account holder form
    if (state.account_holder.is_dirty) {
      account_holder_has_error = Object.keys(fields_validation).some(
        hasFormError("account_holder")
      );
    }

    // Check new passenger form
    if (state.new_passenger) {
      new_passenger_has_error = Object.keys(fields_validation).some(
        hasFormError("new_passenger")
      );
    }

    return [account_holder_has_error, new_passenger_has_error];
  };

  const hasNewPassengerName = () =>
    state.new_passenger &&
    (state.new_passenger.first_name.value.trim() ||
      state.new_passenger.last_name.value.trim());

  const submitForm = () => {
    dispatch({ type: "reset_submit" });
    const [account_holder_has_error, new_passenger_has_error] = validateForms();

    if (state.account_holder.is_dirty && !account_holder_has_error) {
      // Update account holder
      updateUser(state.account_holder);
    }

    if (state.new_passenger) {
      // Delete if no name provided
      if (!hasNewPassengerName()) {
        dispatch({ type: "delete_new_passenger" });
      }

      // Save new passenger
      if (!new_passenger_has_error) {
        addPassenger(state.new_passenger as any);
      } else {
        showPassengersError({ type: "MissingInfoNewPassenger" });
      }
    }

    const dirty_passengers = state.passengers.filter(
      passenger => passenger.is_dirty
    );
    dirty_passengers.map(passenger => {
      const passenger_error = Object.keys(fields_validation).some(
        hasFormError("passenger", passenger)
      );
      if (!passenger_error) {
        updatePassenger(passenger);
      }
    });
  };

  const askDeletePassengerConfirmation = (passenger: PassengerFormType) => {
    reduxDispatch(
      openModal({
        type: "delete_passenger",
        onConfirm: () => deletePassenger(passenger)
      })
    );
  };

  const deletePassenger = (passenger: { id: string | number }) => {
    dispatch({ type: "load" });

    api
      .deletePassenger(passenger)
      .catch(showPassengersError)
      .then(() => {
        tracker.track(removedPassengerProfile());
        dispatch({ type: "delete_passenger", passenger_id: passenger.id });
      });
  };

  const isFormDirty = () =>
    state.account_holder.is_dirty ||
    !!state.new_passenger ||
    state.passengers.some(p => p.is_dirty);

  return (
    <AccountLayout current_page="passengers">
      <div className="flex flex-col gap-300">
        <div className="flex flex-col gap-600">
          <div className="flex flex-col gap-300">
            <AccountVerification user={props.user} />
            {!!state.submit_status_user && (
              <Alert severity={getSeverity(state.submit_status_user)}>
                {getAlertMessage(translator, state.submit_status_user)}
              </Alert>
            )}
            <Label as="span" size="lg" fontWeight="bold">
              {translator.t("!accounts.passengers.account-holder")}
            </Label>
            <PassengerForm
              {...state.account_holder}
              placeholder_name={state.account_holder.full_name}
              handleChange={handleChangeValue("account_holder")}
            />
          </div>
          <AddPassenger
            onAddPassenger={() => {
              dispatch({ type: "create_passenger" });
              add_passenger_form_ref.current?.scrollIntoView?.({});
            }}
            button_disabled={!!state.new_passenger}
          />
        </div>

        {!!state.submit_status_passengers && (
          <Alert severity="negative">
            {getAlertMessage(translator, state.submit_status_passengers)}
          </Alert>
        )}

        {state.passengers.map(passenger => (
          <PassengerForm
            key={passenger.id}
            handleChange={handleChangeValue("passenger", passenger.id)}
            onDelete={() => askDeletePassengerConfirmation(passenger)}
            is_default_expanded={false}
            {...passenger}
          />
        ))}
      </div>

      <div ref={add_passenger_form_ref}>
        {!!state.new_passenger && (
          <div className="mt-300">
            <PassengerForm
              placeholder_name={new_passenger_placeholder_name}
              handleChange={handleChangeValue("new_passenger")}
              onDelete={() => dispatch({ type: "delete_new_passenger" })}
              {...state.new_passenger}
            />
          </div>
        )}
      </div>

      {isFormDirty() && (
        <Button
          id="submit"
          appearance="primary"
          variant="contained"
          size="md"
          className="mt-300 self-start"
          onClick={submitForm}
          isDisabled={state.is_loading}
        >
          {translator.t("!accounts.passengers.save")}
        </Button>
      )}
    </AccountLayout>
  );
};
